import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Lock, Package, Calendar, Hash, Building2 } from 'lucide-react';

const fmtEuro = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const fmtNum = (n, d = 2) => Number(n || 0).toFixed(d);

/**
 * InboxDdtViewer — anteprima read-only di un DDT ricevuto (snapshot inbox).
 * Mostra intestazione mittente, dati trasporto, righe articoli e totali.
 * Nessuna possibilità di editing — i prezzi/qtà sono quelli inviati dall'altra azienda.
 */
const InboxDdtViewer = ({ open, onClose, entry }) => {
  if (!entry) return null;
  const snap = entry.ddt_snapshot || {};
  const items = snap.items || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="inbox-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span>DDT ricevuto da <b data-testid="inbox-view-sender">{entry.from_company_name}</b></span>
            <Badge variant="secondary" className="ml-auto font-mono text-xs" data-testid="inbox-view-number">
              N° {snap.number}/{snap.year}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Anteprima in sola lettura — non puoi modificare questo documento perché è stato emesso da un'altra azienda.
          </DialogDescription>
        </DialogHeader>

        {/* Meta: mittente e date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Mittente
            </div>
            <div className="font-semibold">{entry.from_company_name}</div>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Data documento
            </div>
            <div className="font-mono">{snap.date ? String(snap.date).slice(0, 10) : '—'}</div>
          </div>
          {snap.causale && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Causale</div>
              <div>{snap.causale}</div>
            </div>
          )}
          {snap.transport_date && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Data trasporto</div>
              <div className="font-mono">{String(snap.transport_date).slice(0, 10)} {snap.transport_time || ''}</div>
            </div>
          )}
          {snap.vettore && (
            <div className="space-y-1 md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Vettore</div>
              <div>{snap.vettore}</div>
            </div>
          )}
        </div>

        {/* Items table */}
        <div className="border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]"><Hash className="inline w-3 h-3" /> Cod.</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="text-right w-[70px]">Colli</TableHead>
                <TableHead className="text-right w-[70px]">Pz/Kg</TableHead>
                <TableHead className="text-right w-[90px]">Prezzo</TableHead>
                <TableHead className="text-right w-[60px]">IVA</TableHead>
                <TableHead className="text-right w-[100px]">Totale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-4">Nessun articolo</TableCell></TableRow>
              ) : items.map((it, i) => (
                <TableRow key={i} data-testid={`inbox-view-item-${i}`}>
                  <TableCell className="font-mono text-xs">{it.product_code || '—'}</TableCell>
                  <TableCell className="text-sm">{it.description}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtNum(it.boxes, 1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtNum(it.pieces, it.unit === 'kg' ? 2 : 0)} {it.unit || ''}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtEuro(it.price)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtNum(it.iva, 0)}%</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{fmtEuro(it.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Totali */}
        <div className="flex justify-end">
          <div className="text-right space-y-1 min-w-[220px]" data-testid="inbox-view-totals">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span><Package className="inline w-3 h-3 mr-1" />Colli totali</span>
              <span className="font-mono">{fmtNum(snap.total_boxes, 1)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Pezzi/Kg totali</span>
              <span className="font-mono">{fmtNum(snap.total_pieces, 2)}</span>
            </div>
            <div className="flex justify-between pt-1.5 border-t text-base font-semibold">
              <span>Totale documento</span>
              <span className="font-mono" data-testid="inbox-view-total-amount">{fmtEuro(snap.total_amount)}</span>
            </div>
          </div>
        </div>

        {snap.note && (
          <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs">
            <div className="font-semibold text-amber-900 mb-0.5">Note dal mittente</div>
            <div className="text-amber-900 whitespace-pre-wrap">{snap.note}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InboxDdtViewer;
